import React, { useMemo, useState, useRef } from 'react';
import { 
  DndContext, 
  DragOverlay, 
  closestCorners, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors, 
  DragStartEvent, 
  DragOverEvent, 
  DragEndEvent, 
  defaultDropAnimationSideEffects,
  DropAnimation,
  MeasuringStrategy
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { ArrowLeft, ToggleLeft, X, Wrench, Upload, Plus, Trash2, Layers, ChevronDown, ChevronRight, Copy, ClipboardPaste, Search, Check, FileSpreadsheet, Settings, FileJson, Image as ImageIcon, Star, AlertCircle, RotateCcw, Globe, Coins, Download, Edit2 } from 'lucide-react';
// Mock framer-motion to avoid dependency
const AnimatePresence = ({ children }: { children: React.ReactNode }) => <>{children}</>;
const motion = {
  div: ({ children, ...props }: any) => <div {...props}>{children}</div>,
  circle: ({ children, ...props }: any) => <circle {...props}>{children}</circle>,
};
import { MenuData, ChoiceGroup, ChoiceOption } from '../types';
import { useToast } from './ToastProvider';
import { MenuCanvas } from './MenuCanvas';
import { OrderingPage, OrderingPageRef } from './OrderingPage';
import { importFromExcel } from '../services/excelService';
import { generatePdf } from '../services/pdfService';
import { exportToExcel, exportToExcelTemplate2, exportBothTemplates } from '../../../services/excelService';
import { exportMenuBundle } from '../../../services/menuExport';

type HealthReason = 'image' | 'name-en' | 'name-ar' | 'description-en' | 'description-ar';

interface HealthIssue {
    itemId: string;
    categoryName: string;
    itemName: string;
    reasons: HealthReason[];
}

interface MenuDetailsProps {
    menuName: string;
    data: MenuData;
    onBack: () => void;
    onStatusChange: (status: 'live' | 'offline') => void;
    onOpenEditor: (itemId?: string) => void;
    onUpdateData?: (data: MenuData) => void;
    onExport?: () => void;
    onOpenImageEditor?: () => void;
}

export function MenuDetails({ menuName, data, onBack, onStatusChange, onOpenEditor, onUpdateData, onExport, onOpenImageEditor }: MenuDetailsProps) {
    const printRef = useRef<HTMLDivElement>(null);
    const [language, setLanguage] = useState<'en' | 'ar'>('en');
    const [canvasView, setCanvasView] = useState<'editor' | 'preview'>('editor');
    const [modalType, setModalType] = useState<'image' | 'name' | 'description' | null>(null);
    const [showExportModal, setShowExportModal] = useState(false);
    const [activeTab, setActiveTab] = useState<'home' | 'categories' | 'items' | 'options' | 'canvas' | 'settings'>('home');
    const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ type: 'category' | 'item', id: string, parentId?: string } | null>(null);
    const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
    const [isCreatingCategory, setIsCreatingCategory] = useState(false);
    const [categoryName, setCategoryName] = useState('');
    const [categoryDescription, setCategoryDescription] = useState('');
    const [categoryNameAr, setCategoryNameAr] = useState('');
    const [categoryDescriptionAr, setCategoryDescriptionAr] = useState('');
    const [selectedMenu, setSelectedMenu] = useState('default');
    
    // DnD State
    const [activeId, setActiveId] = useState<string | null>(null);
    
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const toggleCategoryCollapse = (categoryId: string) => {
        const newCollapsed = new Set(collapsedCategories);
        if (newCollapsed.has(categoryId)) {
            newCollapsed.delete(categoryId);
        } else {
            newCollapsed.add(categoryId);
        }
        setCollapsedCategories(newCollapsed);
    };

    const handleDeleteClick = (type: 'category' | 'item', id: string, parentId?: string) => {
        setDeleteConfirmation({ type, id, parentId });
    };

    const confirmDelete = () => {
        if (!deleteConfirmation) return;
        const { type, id, parentId } = deleteConfirmation;
        
        if (type === 'category') {
            if (onUpdateData) {
                const newCategories = data.categories.filter(c => c.id !== id);
                onUpdateData({ ...data, categories: newCategories });
                showToast('Category deleted');
            }
        } else if (type === 'item' && parentId) {
            if (onUpdateData) {
                const newCategories = data.categories.map(c => {
                    if (c.id === parentId) {
                        return { ...c, items: c.items.filter(i => i.id !== id) };
                    }
                    return c;
                });
                onUpdateData({ ...data, categories: newCategories });
                showToast('Item deleted');
            }
        }
        
        setDeleteConfirmation(null);
    };

    const cancelDelete = () => {
        setDeleteConfirmation(null);
    };

    const findContainer = (id: string) => {
        if (data.categories.find(c => c.id === id)) return 'root';
        return data.categories.find(c => c.items.find(i => i.id === id))?.id;
    };

    const handleDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const handleDragOver = (event: DragOverEvent) => {
        const { active, over } = event;
        const overId = over?.id;

        if (!overId || active.id === overId) return;

        const activeContainer = findContainer(active.id as string);
        let overContainer = findContainer(overId as string);

        if (activeContainer && activeContainer !== 'root' && overContainer === 'root') {
           if (data.categories.find(c => c.id === overId)) {
               overContainer = overId as string;
           }
        }

        if (
          !activeContainer || 
          !overContainer || 
          activeContainer === 'root' || 
          overContainer === 'root'
        ) {
          return;
        }

        if (activeContainer !== overContainer && onUpdateData) {
              const prev = data;
              const activeItems = prev.categories.find(c => c.id === activeContainer)?.items || [];
              const overItems = prev.categories.find(c => c.id === overContainer)?.items || [];
              const activeIndex = activeItems.findIndex(i => i.id === active.id);
              const overIndex = overItems.findIndex(i => i.id === overId);

              let newIndex;
              if (prev.categories.find(c => c.id === overId)) {
                newIndex = overItems.length + 1;
              } else {
                const isBelowOverItem =
                  over &&
                  active.rect.current.translated &&
                  active.rect.current.translated.top > over.rect.top + over.rect.height;

                const modifier = isBelowOverItem ? 1 : 0;
                newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
              }

              const newCategories = prev.categories.map(c => {
                if (c.id === activeContainer) {
                   return { ...c, items: c.items.filter(i => i.id !== active.id) };
                }
                if (c.id === overContainer) {
                   const newItems = [...c.items];
                   const itemMoved = prev.categories.find(cat => cat.id === activeContainer)?.items[activeIndex];
                   if(itemMoved) {
                      newItems.splice(newIndex, 0, itemMoved);
                   }
                   return { ...c, items: newItems };
                }
                return c;
              });

              onUpdateData({ ...prev, categories: newCategories });
        }
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        const activeContainer = findContainer(active.id as string);
        const overContainer = findContainer(over?.id as string);

        if (
          activeContainer &&
          overContainer &&
          activeContainer === overContainer &&
          onUpdateData
        ) {
          if(activeContainer === 'root') {
              const activeIndex = data.categories.findIndex(c => c.id === active.id);
              const overIndex = data.categories.findIndex(c => c.id === over?.id);
              if (activeIndex !== overIndex) {
                onUpdateData({
                  ...data,
                  categories: arrayMove(data.categories, activeIndex, overIndex),
                });
              }
          } else {
              const categoryIndex = data.categories.findIndex(c => c.id === activeContainer);
              const activeIndex = data.categories[categoryIndex].items.findIndex(i => i.id === active.id);
              const overIndex = data.categories[categoryIndex].items.findIndex(i => i.id === over?.id);
              
              if (activeIndex !== overIndex) {
                   const newCategories = [...data.categories];
                   newCategories[categoryIndex] = {
                       ...newCategories[categoryIndex],
                       items: arrayMove(newCategories[categoryIndex].items, activeIndex, overIndex)
                   };
                   onUpdateData({ ...data, categories: newCategories });
              }
          }
        }

        setActiveId(null);
    };
    
    const dropAnimation: DropAnimation = {
        sideEffects: defaultDropAnimationSideEffects({
            styles: {
                active: {
                    opacity: '0.5',
                },
            },
        }),
    };
    
    // Item creation state
    const [isCreatingItem, setIsCreatingItem] = useState(false);
    const [createItemTab, setCreateItemTab] = useState<'details' | 'options'>('details');
    const [itemName, setItemName] = useState('');
    const [itemDescription, setItemDescription] = useState('');
    const [itemNameAr, setItemNameAr] = useState('');
    const [itemDescriptionAr, setItemDescriptionAr] = useState('');
    const [itemCategory, setItemCategory] = useState('default');
    const [itemPrice, setItemPrice] = useState('');
    const [itemImage, setItemImage] = useState<File | null>(null);
    const [itemChoiceGroups, setItemChoiceGroups] = useState<ChoiceGroup[]>([]);
    const [copiedGroup, setCopiedGroup] = useState<ChoiceGroup | null>(null);

    // Global Options State (Main Tab)
    const [menuChoiceGroups, setMenuChoiceGroups] = useState<(ChoiceGroup & { targetItemIds?: string[] })[]>([]);
    const [itemSelectorModal, setItemSelectorModal] = useState<{ isOpen: boolean; groupId: string | null }>({ isOpen: false, groupId: null });
    const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
    const [itemSearchQuery, setItemSearchQuery] = useState('');
    
    // Category Item Selector State
    const [categoryItemSelectorOpen, setCategoryItemSelectorOpen] = useState(false);
    const [categorySelectedItems, setCategorySelectedItems] = useState<Set<string>>(new Set());

    const orderingRef = useRef<OrderingPageRef>(null);

    // Canvas Filters State
    const [viewFilters, setViewFilters] = useState({
        showImagesOnly: false,
        showNotesOnly: false,
        showIncompleteOnly: false
    });

    const { showToast } = useToast() || { showToast: (msg: string) => console.log(msg) };
    const status = data.status || 'offline';
    const showHealth = true; // Always show health details
    const categories = data.categories || [];
    const includeArabic = data.languages?.includes('ar') || false;

    const totalItems = useMemo(() => categories.reduce((acc, category) => acc + category.items.length, 0), [categories]);

    const healthIssues = useMemo(() => {
        const issues: HealthIssue[] = [];

        categories.forEach((category) => {
            category.items.forEach((item) => {
                const reasons: HealthReason[] = [];
                if (!item.image && !item.imageNotAvailable) reasons.push('image');
                if (!item.name.en?.trim()) reasons.push('name-en');
                if (!item.name.ar?.trim()) reasons.push('name-ar');
                if (!item.description.en?.trim()) reasons.push('description-en');
                if (!item.description.ar?.trim()) reasons.push('description-ar');

                if (reasons.length) {
                    issues.push({
                        itemId: item.id,
                        categoryName: category.name.en || category.name.ar || 'Category',
                        itemName: item.name.en || item.name.ar || 'Unnamed item',
                        reasons,
                    });
                }
            });
        });

        return issues;
    }, [categories]);

    const uniqueIssueItems = useMemo(() => new Set(healthIssues.map((issue) => issue.itemId)).size, [healthIssues]);

    const healthScore = totalItems ? Math.round(((totalItems - uniqueIssueItems) / totalItems) * 100) : 100;

    const missingImageIssues = useMemo(() => healthIssues.filter((issue) => issue.reasons.includes('image')), [healthIssues]);
    const missingNameIssues = useMemo(
        () => healthIssues.filter((issue) => issue.reasons.some((reason) => reason.startsWith('name-'))),
        [healthIssues]
    );
    const missingDescriptionIssues = useMemo(
        () => healthIssues.filter((issue) => issue.reasons.some((reason) => reason.startsWith('description-'))),
        [healthIssues]
    );

    const issueGroups = [
        { label: 'Missing Images', count: missingImageIssues.length, description: 'Items still need photos', type: 'image' as const },
        { label: 'Missing Names', count: missingNameIssues.length, description: 'EN/AR names required', type: 'name' as const },
        { label: 'Missing Descriptions', count: missingDescriptionIssues.length, description: 'EN/AR descriptions required', type: 'description' as const },
    ];

    const modalIssues = useMemo(() => {
        if (!modalType) return [] as HealthIssue[];
        if (modalType === 'image') return missingImageIssues;
        if (modalType === 'name') return missingNameIssues;
        return missingDescriptionIssues;
    }, [modalType, missingImageIssues, missingNameIssues, missingDescriptionIssues]);

    const reasonLabels: Record<HealthReason, string> = {
        image: 'Needs photo',
        'name-en': 'Missing name (EN)',
        'name-ar': 'Missing name (AR)',
        'description-en': 'Missing description (EN)',
        'description-ar': 'Missing description (AR)',
    };

    const progressColor = healthScore === 100 ? 'text-emerald-500' : 'text-purple-600';
    const circleRadius = 60;
    const circleCircumference = 2 * Math.PI * circleRadius;
    
    const handleGoLive = () => {
        // Allow toggling regardless of health score for now, or enforce rules if needed
        // if (status !== 'live' && healthScore < 100) return;
        onStatusChange(status === 'live' ? 'offline' : 'live');
    };

    const resetCategoryForm = () => {
        setCategoryName('');
        setCategoryDescription('');
        setCategoryNameAr('');
        setCategoryDescriptionAr('');
        setSelectedMenu('default');
    };

    const handleOpenCreateCategory = () => {
        resetCategoryForm();
        setIsCreatingCategory(true);
    };

    const handleCancelCreateCategory = () => {
        setIsCreatingCategory(false);
        setEditingCategoryId(null);
        setCategoryName('');
        setCategoryDescription('');
        setCategoryNameAr('');
        setCategoryDescriptionAr('');
    };

    const handleSaveCategory = () => {
        if (!categoryName.trim() || !onUpdateData) return;
        
        if (editingCategoryId) {
            // Update existing
            const newCategories = data.categories.map(c => {
                if (c.id === editingCategoryId) {
                    return {
                        ...c,
                        name: { ...c.name, en: categoryName, ar: categoryNameAr },
                        description: { ...c.description, en: categoryDescription, ar: categoryDescriptionAr }
                    };
                }
                return c;
            });
            onUpdateData({ ...data, categories: newCategories });
            showToast('Category updated');
        } else {
            // Create new
            const newCategory = {
                id: `cat-${Date.now()}`,
                name: { en: categoryName, ar: categoryNameAr },
                description: { en: categoryDescription, ar: categoryDescriptionAr },
                items: []
            };
            onUpdateData({ ...data, categories: [...data.categories, newCategory] });
            showToast('Category created');
        }
        
        setIsCreatingCategory(false);
        setEditingCategoryId(null);
        setCategoryName('');
        setCategoryDescription('');
        setCategoryNameAr('');
        setCategoryDescriptionAr('');
    };

    const resetItemForm = () => {
        setCreateItemTab('details');
        setItemName('');
        setItemDescription('');
        setItemNameAr('');
        setItemDescriptionAr('');
        setItemCategory('default');
        setItemPrice('');
        setItemImage(null);
        setItemChoiceGroups([]);
    };

    // Choice Group Helpers
    const addChoiceGroup = () => {
        const newGroup: ChoiceGroup = {
            id: `cg-${Date.now()}`,
            name: { en: 'Add-ons', ar: 'إضافات' },
            options: [],
            required: false,
            optional: true,
            min: 0,
            max: 1
        };
        setItemChoiceGroups([...itemChoiceGroups, newGroup]);
    };

    const updateChoiceGroup = (groupId: string, field: string, value: any) => {
        setItemChoiceGroups(groups => groups.map(g => {
            if (g.id !== groupId) return g;
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                return { ...g, [parent]: { ...(g as any)[parent], [child]: value } };
            }
            return { ...g, [field]: value };
        }));
    };

    const deleteChoiceGroup = (groupId: string) => {
        setItemChoiceGroups(groups => groups.filter(g => g.id !== groupId));
    };

    const pasteChoiceGroup = () => {
        if (!copiedGroup) return;
        const newGroup: ChoiceGroup = {
            ...copiedGroup,
            id: `cg-${Date.now()}`,
            options: copiedGroup.options.map(opt => ({
                ...opt,
                id: `opt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            }))
        };
        setItemChoiceGroups([...itemChoiceGroups, newGroup]);
    };

    const addOption = (groupId: string) => {
        const newOption: ChoiceOption = {
            id: `opt-${Date.now()}`,
            name: { en: 'Option', ar: 'خيار' },
            priceModifier: 0
        };
        setItemChoiceGroups(groups => groups.map(g => 
            g.id === groupId ? { ...g, options: [...g.options, newOption] } : g
        ));
    };

    const updateOption = (groupId: string, optionId: string, field: string, value: any) => {
        setItemChoiceGroups(groups => groups.map(g => {
            if (g.id !== groupId) return g;
            return {
                ...g,
                options: g.options.map(opt => {
                    if (opt.id !== optionId) return opt;
                    if (field.includes('.')) {
                        const [parent, child] = field.split('.');
                        return { ...opt, [parent]: { ...(opt as any)[parent], [child]: value } };
                    }
                    return { ...opt, [field]: value };
                })
            };
        }));
    };

    const deleteOption = (groupId: string, optionId: string) => {
        setItemChoiceGroups(groups => groups.map(g => 
            g.id === groupId ? { ...g, options: g.options.filter(o => o.id !== optionId) } : g
        ));
    };

    // Menu Level Choice Group Helpers
    const addMenuChoiceGroup = () => {
        const newGroup: ChoiceGroup = {
            id: `cg-${Date.now()}`,
            name: { en: 'Global Add-ons', ar: 'إضافات عامة' },
            options: [],
            required: false,
            optional: true,
            min: 0,
            max: 1
        };
        setMenuChoiceGroups([...menuChoiceGroups, newGroup]);
    };

    const updateMenuChoiceGroup = (groupId: string, field: string, value: any) => {
        setMenuChoiceGroups(groups => groups.map(g => {
            if (g.id !== groupId) return g;
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                return { ...g, [parent]: { ...(g as any)[parent], [child]: value } };
            }
            return { ...g, [field]: value };
        }));
    };

    const deleteMenuChoiceGroup = (groupId: string) => {
        setMenuChoiceGroups(groups => groups.filter(g => g.id !== groupId));
    };

    const pasteMenuChoiceGroup = () => {
        if (!copiedGroup) return;
        const newGroup: ChoiceGroup = {
            ...copiedGroup,
            id: `cg-${Date.now()}`,
            options: copiedGroup.options.map(opt => ({
                ...opt,
                id: `opt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            }))
        };
        setMenuChoiceGroups([...menuChoiceGroups, newGroup]);
    };

    const addMenuOption = (groupId: string) => {
        const newOption: ChoiceOption = {
            id: `opt-${Date.now()}`,
            name: { en: 'Option', ar: 'خيار' },
            priceModifier: 0
        };
        setMenuChoiceGroups(groups => groups.map(g => 
            g.id === groupId ? { ...g, options: [...g.options, newOption] } : g
        ));
    };

    const updateMenuOption = (groupId: string, optionId: string, field: string, value: any) => {
        setMenuChoiceGroups(groups => groups.map(g => {
            if (g.id !== groupId) return g;
            return {
                ...g,
                options: g.options.map(opt => {
                    if (opt.id !== optionId) return opt;
                    if (field.includes('.')) {
                        const [parent, child] = field.split('.');
                        return { ...opt, [parent]: { ...(opt as any)[parent], [child]: value } };
                    }
                    return { ...opt, [field]: value };
                })
            };
        }));
    };

    const deleteMenuOption = (groupId: string, optionId: string) => {
        setMenuChoiceGroups(groups => groups.map(g => 
            g.id === groupId ? { ...g, options: g.options.filter(o => o.id !== optionId) } : g
        ));
    };

    // Item Selector Helpers
    const openItemSelector = (groupId: string, currentIds: string[] = []) => {
        setSelectedItemIds(new Set(currentIds));
        setItemSelectorModal({ isOpen: true, groupId });
        setItemSearchQuery('');
    };

    const toggleItemSelection = (itemId: string) => {
        const newSet = new Set(selectedItemIds);
        if (newSet.has(itemId)) {
            newSet.delete(itemId);
        } else {
            newSet.add(itemId);
        }
        setSelectedItemIds(newSet);
    };

    const saveItemSelection = () => {
        if (!itemSelectorModal.groupId) return;
        updateMenuChoiceGroup(itemSelectorModal.groupId, 'targetItemIds', Array.from(selectedItemIds));
        setItemSelectorModal({ isOpen: false, groupId: null });
    };

    // Category Item Selector Helpers
    const openCategoryItemSelector = () => {
        setCategoryItemSelectorOpen(true);
        setItemSearchQuery('');
        // In a real app, you might want to pre-select items already in the category
        // For now, we start fresh or keep existing selection state
    };

    const toggleCategoryItemSelection = (itemId: string) => {
        const newSet = new Set(categorySelectedItems);
        if (newSet.has(itemId)) {
            newSet.delete(itemId);
        } else {
            newSet.add(itemId);
        }
        setCategorySelectedItems(newSet);
    };

    const saveCategoryItemSelection = () => {
        setCategoryItemSelectorOpen(false);
        showToast(`${categorySelectedItems.size} items added to category`, 'success');
    };

    const handleOpenCreateItem = () => {
        resetItemForm();
        setIsCreatingItem(true);
    };

    const handleCancelCreateItem = () => {
        setIsCreatingItem(false);
    };

    const handleSaveItem = () => {
        if (!itemName.trim() || itemCategory === 'default') return;
        setIsCreatingItem(false);
        showToast('Item saved (demo only)', 'success');
    };

    const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const importedData = await importFromExcel(file);
            if (onUpdateData) {
                onUpdateData(importedData);
                alert('Menu imported successfully!');
            }
        } catch (error) {
            console.error('Error importing file:', error);
            alert('Error importing file. Please check the format.');
        }
    };

    const handleDownloadJson = () => {
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${data.title?.en || "menu"}_backup_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleDownloadPdf = async () => {
        await generatePdf('printable-canvas', menuName || 'menu');
    };

    return (
        <div className="h-screen flex flex-col bg-slate-50 overflow-hidden">
            {/* Top Navigation Bar */}
            <div className="px-6 py-4 shrink-0">
                <button
                    onClick={onBack}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium text-sm shadow-sm transition-colors"
                >
                    <ArrowLeft size={16} />
                    <span>Back to Menus</span>
                </button>
            </div>

            {/* Fixed Header Section */}
            <div className="shrink-0 mx-auto w-full max-w-[98%] space-y-4 z-20 pt-2">
                {/* Menu Header Card */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-4 w-full md:w-auto">
                        {data.logo ? (
                            <div className="w-14 h-14 rounded-xl overflow-hidden bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                                <img src={data.logo} alt={menuName} className="w-full h-full object-contain" />
                            </div>
                        ) : (
                            <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 shrink-0">
                                <ImageIcon size={24} />
                            </div>
                        )}
                        <div>
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-0.5">Menu</span>
                            <h1 className="text-xl font-bold text-slate-900">{menuName}</h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 no-scrollbar">
                        <div className="flex bg-slate-100/50 p-1 rounded-full">
                            {['home', 'categories', 'items', 'options', 'canvas', 'settings'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab as any)}
                                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                                        activeTab === tab
                                            ? 'bg-slate-900 text-white shadow-sm'
                                            : 'text-slate-500 hover:text-slate-900 hover:bg-slate-200/50'
                                    }`}
                                >
                                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                </button>
                            ))}
                        </div>
                        
                        {onExport && (
                            <>
                                <div className="h-6 w-px bg-slate-200 mx-2"></div>
                                <button
                                    onClick={() => setShowExportModal(true)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 transition-colors text-sm font-bold whitespace-nowrap"
                                >
                                    <FileSpreadsheet size={16} />
                                    Export
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Canvas Filters - Only visible when Canvas tab is active */}
                {activeTab === 'canvas' && (
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                        <div className="px-6 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex bg-white rounded-lg border border-slate-200 p-1 mr-4">
                                        <button
                                            onClick={() => setCanvasView('editor')}
                                            className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${canvasView === 'editor' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-900'}`}
                                        >
                                            Editor
                                        </button>
                                        <button
                                            onClick={() => setCanvasView('preview')}
                                            className={`px-3 py-1 rounded-md text-xs font-bold transition-colors ${canvasView === 'preview' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-900'}`}
                                        >
                                            Final Menu
                                        </button>
                                    </div>

                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider mr-2">Filters:</span>
                                    
                                    <button 
                                        onClick={() => setViewFilters(prev => ({ ...prev, showImagesOnly: !prev.showImagesOnly }))}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${viewFilters.showImagesOnly ? 'bg-orange-100 text-orange-700 border border-orange-200' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
                                    >
                                        {viewFilters.showImagesOnly ? <Check size={14} /> : <ImageIcon size={14} />}
                                        Images Only
                                    </button>

                                    <button 
                                        onClick={() => setViewFilters(prev => ({ ...prev, showNotesOnly: !prev.showNotesOnly }))}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${viewFilters.showNotesOnly ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
                                    >
                                        {viewFilters.showNotesOnly ? <Check size={14} /> : <Star size={14} />}
                                        Notes Only
                                    </button>

                                    <button 
                                        onClick={() => setViewFilters(prev => ({ ...prev, showIncompleteOnly: !prev.showIncompleteOnly }))}
                                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${viewFilters.showIncompleteOnly ? 'bg-red-100 text-red-700 border border-red-200' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
                                    >
                                        {viewFilters.showIncompleteOnly ? <Check size={14} /> : <AlertCircle size={14} />}
                                        Incomplete
                                    </button>
                                </div>

                                {canvasView === 'editor' && (
                                    <div className="flex items-center gap-3">
                                        <button 
                                            onClick={handleDownloadPdf}
                                            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                                        >
                                            <Download size={14} />
                                            Download PDF
                                        </button>
                                    </div>
                                )}

                                {canvasView === 'preview' && (
                                    <div className="flex items-center gap-3">
                                        <div className="h-4 w-px bg-slate-300 mx-2"></div>

                                        <button 
                                            onClick={() => orderingRef.current?.reset()}
                                            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
                                        >
                                            <RotateCcw size={14} />
                                            Reset
                                        </button>

                                        <button 
                                            onClick={() => orderingRef.current?.exportLive()}
                                            className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                                        >
                                            <FileSpreadsheet size={14} />
                                            Export Live
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
            </div>

            <section className="flex-1 min-h-0 overflow-hidden mx-auto w-full max-w-[98%] flex flex-col">
                <div key={activeTab} className="flex-1 overflow-y-auto space-y-6 pb-32 pt-6">
                        {activeTab === 'home' && (
                            <div className="space-y-6">
                                {/* Health & Status Card */}
                                <motion.div 
                                    layout
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
                                >
                                    <div className="flex items-center justify-between p-8">
                                        {/* Left Side: Health Circle & Stats */}
                                        <div className="flex items-center gap-8">
                                            {/* Circular Progress */}
                                            <div className="relative w-40 h-40 flex items-center justify-center shrink-0">
                                                <svg className="w-full h-full transform -rotate-90">
                                                    <circle cx="50%" cy="50%" r={circleRadius} fill="transparent" stroke="#f1f5f9" strokeWidth="12" />
                                                    <motion.circle
                                                        cx="50%"
                                                        cy="50%"
                                                        r={circleRadius}
                                                        fill="transparent"
                                                        stroke="currentColor"
                                                        strokeWidth="12"
                                                        strokeDasharray={circleCircumference}
                                                        initial={{ strokeDashoffset: circleCircumference }}
                                                        animate={{ strokeDashoffset: circleCircumference - (healthScore / 100) * circleCircumference }}
                                                        transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
                                                        strokeLinecap="round"
                                                        className={progressColor}
                                                    />
                                                </svg>
                                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                    <span className="text-3xl font-bold text-slate-900">{healthScore}%</span>
                                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Healthy</span>
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                <div>
                                                    <p className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Menu Health</p>
                                                    <p className="text-xs text-slate-400">{totalItems} item{totalItems === 1 ? '' : 's'} evaluated</p>
                                                </div>
                                                
                                                {/* Chips */}
                                                <div className="flex flex-wrap gap-2">
                                                    {issueGroups.map((group) => (
                                                        <button
                                                            key={group.type}
                                                            onClick={() => group.count > 0 && setModalType(group.type)}
                                                            disabled={group.count === 0}
                                                            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                                                                group.count > 0 
                                                                ? 'bg-red-50 text-red-600 border-red-100 hover:bg-red-100 cursor-pointer' 
                                                                : 'bg-emerald-50 text-emerald-600 border-emerald-100 opacity-60 cursor-default'
                                                            }`}
                                                        >
                                                            {group.label}: {group.count}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Right Side: Go Live Action */}
                                        <div className="flex flex-col items-end gap-3 shrink-0 ml-auto">
                                            <button
                                                onClick={handleGoLive}
                                                disabled={status !== 'live' && healthScore < 100}
                                                className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all shadow-sm active-scale-95 ${
                                                    status === 'live'
                                                        ? 'bg-white border-2 border-slate-200 text-slate-600 hover:border-slate-300'
                                                        : healthScore < 100
                                                            ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                                            : 'bg-slate-900 text-white hover:bg-slate-800'
                                                }`}
                                            >
                                                <ToggleLeft size={24} className={`transition-transform duration-300 ${status === 'live' ? 'rotate-0 text-emerald-500' : 'rotate-180 text-slate-400'}`} />
                                                <span className="text-lg">{status === 'live' ? 'Live' : 'Go Live'}</span>
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Analytics Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                            <Layers size={24} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-slate-500">Categories</p>
                                            <p className="text-2xl font-bold text-slate-900">{categories.length}</p>
                                        </div>
                                    </div>
                                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-600 flex items-center justify-center">
                                            <Search size={24} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-slate-500">Total Items</p>
                                            <p className="text-2xl font-bold text-slate-900">{totalItems}</p>
                                        </div>
                                    </div>
                                    <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center">
                                            <Settings size={24} />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-slate-500">Global Options</p>
                                            <p className="text-2xl font-bold text-slate-900">{menuChoiceGroups.length}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'categories' && !isCreatingCategory && (
                            categories.length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-8 py-10 text-center">
                                    <h2 className="text-lg font-semibold text-slate-900 mb-2">No categories yet</h2>
                                    <p className="text-sm text-slate-500 mb-6 max-w-xl mx-auto">
                                        Start by grouping dishes into sections like breakfast, mains, or desserts so your menu is easier to explore.
                                    </p>
                                    <button
                                        onClick={handleOpenCreateCategory}
                                        className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-sm hover:bg-slate-800 transition-colors"
                                    >
                                        Create new category
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-8">
                                    <div className="flex justify-between items-center">
                                        <h2 className="text-lg font-semibold text-slate-900">Categories ({categories.length})</h2>
                                        <button
                                            onClick={handleOpenCreateCategory}
                                            className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-sm hover:bg-slate-800 transition-colors"
                                        >
                                            Add Category
                                        </button>
                                    </div>
                                    
                                    {categories.map((cat) => (
                                        <div key={cat.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                            {/* Category Header */}
                                            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                                                <div>
                                                    <h3 className="font-bold text-lg text-slate-900">{cat.name.en}</h3>
                                                    {cat.description?.en && <p className="text-sm text-slate-500 mt-0.5">{cat.description.en}</p>}
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <span className="text-sm text-slate-500">{cat.items.length} items</span>
                                                    <button 
                                                        onClick={() => {
                                                            setCategoryName(cat.name.en);
                                                            setCategoryDescription(cat.description?.en || '');
                                                            setCategoryNameAr(cat.name.ar || '');
                                                            setCategoryDescriptionAr(cat.description?.ar || '');
                                                            setEditingCategoryId(cat.id);
                                                            setIsCreatingCategory(true);
                                                        }}
                                                        className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteClick('category', cat.id)}
                                                        className="p-2 text-slate-400 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                    <button 
                                                        onClick={() => toggleCategoryCollapse(cat.id)}
                                                        className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
                                                    >
                                                        {collapsedCategories.has(cat.id) ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Items Grid */}
                                            {!collapsedCategories.has(cat.id) && (
                                                <div className="p-6">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        {cat.items.map((item) => (
                                                            <div key={item.id} className="flex gap-4 p-3 rounded-lg border border-slate-100 hover:border-slate-300 transition-all bg-white group">
                                                                {/* Image */}
                                                                <div className="w-20 h-20 shrink-0 bg-slate-100 rounded-md overflow-hidden">
                                                                    {item.image ? (
                                                                        <img src={item.image} alt={item.name.en} className="w-full h-full object-cover" />
                                                                    ) : (
                                                                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                                                                            <ImageIcon size={20} />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* Content */}
                                                                <div className="flex-1 min-w-0 flex flex-col">
                                                                    <div className="flex justify-between items-start">
                                                                        <h4 className="font-semibold text-slate-900 truncate pr-2">{item.name.en}</h4>
                                                                    </div>
                                                                    
                                                                    {item.description?.en && (
                                                                        <p className="text-xs text-slate-500 line-clamp-2 mt-1 mb-auto">{item.description.en}</p>
                                                                    )}
                                                                    
                                                                    <div className="mt-2 flex items-center justify-between">
                                                                        <span className="text-sm font-medium text-slate-900">QAR {item.price.toFixed(2)}</span>
                                                                        
                                                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                             <button 
                                                                                onClick={() => onOpenEditor(item.id)}
                                                                                className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50"
                                                                             >
                                                                                 <Edit2 size={14} />
                                                                             </button>
                                                                             <button 
                                                                                onClick={() => handleDeleteClick('item', item.id, cat.id)}
                                                                                className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-red-50"
                                                                             >
                                                                                 <Trash2 size={14} />
                                                                             </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Category Actions Footer */}
                                                    <div className="mt-6 pt-4 border-t border-dashed border-slate-200 flex justify-end gap-3">
                                                        <button
                                                            onClick={() => {
                                                                openCategoryItemSelector();
                                                            }}
                                                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-emerald-600 hover:bg-emerald-50 transition-colors"
                                                        >
                                                            <Plus size={16} />
                                                            Add existing items
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setActiveTab('items');
                                                                handleOpenCreateItem();
                                                            }}
                                                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                                                        >
                                                            <Plus size={16} />
                                                            Create new item
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )
                        )}

                        {activeTab === 'categories' && isCreatingCategory && (
                            <div className="rounded-2xl border border-slate-200 bg-white px-6 py-6 space-y-6">
                                <div className="flex items-center justify-between">
                                    <button
                                        onClick={handleCancelCreateCategory}
                                        className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800"
                                    >
                                        <ArrowLeft size={18} />
                                        <span>Categories</span>
                                    </button>
                                    <div className="text-right">
                                        <p className="text-xs font-semibold tracking-wide text-slate-400 uppercase">Category</p>
                                        <p className="text-sm font-bold text-slate-900">Create new category</p>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                                        <div className="space-y-4">
                                            {/* Row 1: Name / Arabic Name */}
                                            <div className={`grid grid-cols-1 ${includeArabic ? 'md:grid-cols-2' : ''} gap-4`}>
                                                <div className="space-y-1.5">
                                                    <label className="block text-sm font-semibold text-slate-800">
                                                        Name<span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={categoryName}
                                                        onChange={(e) => setCategoryName(e.target.value)}
                                                        placeholder="Morning favorites, Weekend specials..."
                                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white"
                                                    />
                                                    <p className="text-xs text-slate-500">
                                                        Use a short label that helps guests quickly recognize the type of dishes.
                                                    </p>
                                                </div>

                                                {includeArabic && (
                                                    <div className="space-y-1.5">
                                                        <label className="block text-sm font-semibold text-slate-800">Arabic name</label>
                                                        <input
                                                            type="text"
                                                            value={categoryNameAr}
                                                            onChange={(e) => setCategoryNameAr(e.target.value)}
                                                            placeholder="اكتب اسم القسم باللغة العربية"
                                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white"
                                                        />
                                                        <p className="text-xs text-slate-500">
                                                            Provide a natural-sounding label for guests reading in Arabic.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Row 2: Description / Arabic Description */}
                                            <div className={`grid grid-cols-1 ${includeArabic ? 'md:grid-cols-2' : ''} gap-4`}>
                                                <div className="space-y-1.5">
                                                    <label className="block text-sm font-semibold text-slate-800">Description</label>
                                                    <textarea
                                                        value={categoryDescription}
                                                        onChange={(e) => setCategoryDescription(e.target.value)}
                                                        placeholder="Describe what diners can expect from this section."
                                                        rows={3}
                                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white resize-none"
                                                    />
                                                    <p className="text-xs text-slate-500">
                                                        Optional details like portion style or dietary focus can go here.
                                                    </p>
                                                </div>

                                                {includeArabic && (
                                                    <div className="space-y-1.5">
                                                        <label className="block text-sm font-semibold text-slate-800">Arabic description</label>
                                                        <textarea
                                                            value={categoryDescriptionAr}
                                                            onChange={(e) => setCategoryDescriptionAr(e.target.value)}
                                                            placeholder="أضف وصفًا موجزًا للقسم باللغة العربية إن رغبت."
                                                            rows={3}
                                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white resize-none"
                                                        />
                                                        <p className="text-xs text-slate-500">
                                                            Optional notes that help Arabic-speaking guests understand the section.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-1.5">
                                        <label className="block text-sm font-semibold text-slate-800">Menus</label>
                                        <select
                                            value={selectedMenu}
                                            onChange={(e) => setSelectedMenu(e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white"
                                        >
                                            <option value="default">Default menu</option>
                                        </select>
                                        <p className="text-xs text-slate-500">
                                            Categories should be linked to at least one menu so they appear where guests are ordering.
                                        </p>
                                    </div>
                                </div>

                                <div className="border-t border-slate-100 pt-6 space-y-4">
                                    <div className="flex items-center justify-between mb-2">
                                        <div>
                                            <p className="text-sm font-semibold text-slate-800">Add items</p>
                                            <p className="text-xs text-slate-500">
                                                Attach dishes to this category now or come back later.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                                        <input
                                            type="text"
                                            placeholder="Find items by name, barcode, or PLU code"
                                            className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white"
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setActiveTab('items');
                                                    handleOpenCreateItem();
                                                }}
                                                className="px-4 py-2 rounded-full text-sm font-semibold bg-slate-900 text-white shadow-sm hover:bg-slate-800"
                                            >
                                                Create new item
                                            </button>
                                            <button
                                                type="button"
                                                onClick={openCategoryItemSelector}
                                                className="px-4 py-2 rounded-full text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
                                            >
                                                Add existing items
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Selected Items Preview */}
                                    {categorySelectedItems.size > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {Array.from(categorySelectedItems).map((itemId: string) => {
                                                let itemName = 'Unknown Item';
                                                categories.forEach(c => {
                                                    const found = c.items.find(i => i.id === itemId);
                                                    if (found) itemName = found.name.en;
                                                });
                                                return (
                                                    <span key={itemId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200">
                                                        {itemName}
                                                        <button 
                                                            onClick={() => toggleCategoryItemSelection(itemId)}
                                                            className="hover:text-red-500"
                                                        >
                                                            <X size={12} />
                                                        </button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 mt-2">
                                    <button
                                        type="button"
                                        onClick={handleCancelCreateCategory}
                                        className="px-4 py-2 rounded-full text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSaveCategory}
                                        disabled={!categoryName.trim()}
                                        className={`px-5 py-2 rounded-full text-sm font-semibold shadow-sm transition-colors ${
                                            categoryName.trim()
                                                ? 'bg-slate-900 text-white hover:bg-slate-800'
                                                : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                        }`}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        )}

                        {activeTab === 'items' && !isCreatingItem && (
                            totalItems === 0 ? (
                                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-8 py-10 text-center">
                                    <h2 className="text-lg font-semibold text-slate-900 mb-2">No items to show yet</h2>
                                    <p className="text-sm text-slate-500 mb-6 max-w-xl mx-auto">
                                        Once you start creating dishes, they will appear here so you can review and organize them.
                                    </p>
                                    <button
                                        onClick={handleOpenCreateItem}
                                        className="inline-flex items-center justify-center px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-sm hover:bg-slate-800 transition-colors"
                                    >
                                        Add item
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="flex justify-between items-center">
                                        <h2 className="text-lg font-semibold text-slate-900">All Items ({totalItems})</h2>
                                        <button
                                            onClick={handleOpenCreateItem}
                                            className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-sm hover:bg-slate-800 transition-colors"
                                        >
                                            Add Item
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4">
                                        {categories.flatMap(cat => cat.items.map(item => ({ ...item, categoryName: cat.name.en, categoryId: cat.id }))).map((item) => (
                                            <div key={item.id} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex gap-4 items-center hover:border-slate-300 transition-colors">
                                                <div className="w-16 h-16 bg-slate-100 rounded-lg flex-shrink-0 overflow-hidden">
                                                    {item.image ? (
                                                        <img src={item.image} alt={item.name.en} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-slate-400">
                                                            <ImageIcon size={24} />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <h3 className="font-semibold text-slate-900 truncate">{item.name.en}</h3>
                                                            <p className="text-sm text-slate-500">{item.categoryName}</p>
                                                        </div>
                                                        <span className="font-medium text-slate-900">
                                                            {item.price} QAR
                                                        </span>
                                                    </div>
                                                    {item.description?.en && (
                                                        <p className="text-sm text-slate-600 line-clamp-1 mt-1">{item.description.en}</p>
                                                    )}
                                                </div>
                                                <div className="flex gap-2 ml-4">
                                                    <button 
                                                        onClick={() => onOpenEditor(item.id)}
                                                        className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteClick('item', item.id, item.categoryId)}
                                                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )
                        )}

                        {activeTab === 'items' && isCreatingItem && (
                            <div className="rounded-2xl border border-slate-200 bg-white px-6 py-6 space-y-6">
                                {/* Header */}
                                <div className="flex items-center justify-between">
                                    <button
                                        onClick={handleCancelCreateItem}
                                        className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800"
                                    >
                                        <ArrowLeft size={18} />
                                        <span>Items</span>
                                    </button>
                                    <div className="text-right">
                                        <p className="text-xs font-semibold tracking-wide text-slate-400 uppercase">Item</p>
                                        <p className="text-sm font-bold text-slate-900">Create new item</p>
                                    </div>
                                </div>

                                {/* Tab Switcher */}
                                <div className="flex gap-6 border-b border-slate-200">
                                    <button
                                        onClick={() => setCreateItemTab('details')}
                                        className={`pb-3 text-sm font-semibold transition-colors relative ${
                                            createItemTab === 'details' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'
                                        }`}
                                    >
                                        Details
                                        {createItemTab === 'details' && (
                                            <motion.div layoutId="activeTabItem" className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-900" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setCreateItemTab('options')}
                                        className={`pb-3 text-sm font-semibold transition-colors relative ${
                                            createItemTab === 'options' ? 'text-slate-900' : 'text-slate-500 hover:text-slate-700'
                                        }`}
                                    >
                                        Options
                                        {createItemTab === 'options' && (
                                            <motion.div layoutId="activeTabItem" className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-900" />
                                        )}
                                    </button>
                                </div>

                                {/* Form */}
                                <div className="space-y-6 max-w-3xl mx-auto">
                                    {createItemTab === 'details' && (
                                        <div className="space-y-6">
                                    {/* Image Upload */}
                                    <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 text-center hover:bg-slate-100 transition-colors cursor-pointer relative">
                                        <input 
                                            type="file" 
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                            accept="image/*"
                                            onChange={(e) => {
                                                if (e.target.files?.[0]) setItemImage(e.target.files[0]);
                                            }}
                                        />
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-500">
                                                <Upload size={24} />
                                            </div>
                                            {itemImage ? (
                                                <p className="text-sm font-semibold text-emerald-600">{itemImage.name}</p>
                                            ) : (
                                                <>
                                                    <p className="text-sm font-semibold text-slate-900">Drop an image here or click to upload</p>
                                                    <p className="text-xs text-slate-500">Supports JPG, PNG, WEBP</p>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 gap-6">
                                        {/* Name */}
                                        <div className={`grid grid-cols-1 ${includeArabic ? 'md:grid-cols-2' : ''} gap-4`}>
                                            <div className="space-y-1.5">
                                                <label className="block text-sm font-semibold text-slate-800">
                                                    Name<span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={itemName}
                                                    onChange={(e) => setItemName(e.target.value)}
                                                    placeholder="e.g. Classic Cheeseburger"
                                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white"
                                                />
                                            </div>

                                            {includeArabic && (
                                                <div className="space-y-1.5">
                                                    <label className="block text-sm font-semibold text-slate-800">Arabic name</label>
                                                    <input
                                                        type="text"
                                                        value={itemNameAr}
                                                        onChange={(e) => setItemNameAr(e.target.value)}
                                                        placeholder="اكتب اسم العنصر باللغة العربية"
                                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Description */}
                                        <div className={`grid grid-cols-1 ${includeArabic ? 'md:grid-cols-2' : ''} gap-4`}>
                                            <div className="space-y-1.5">
                                                <label className="block text-sm font-semibold text-slate-800">Description</label>
                                                <textarea
                                                    value={itemDescription}
                                                    onChange={(e) => setItemDescription(e.target.value)}
                                                    placeholder="Briefly explain the ingredients and preparation..."
                                                    rows={3}
                                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white resize-none"
                                                />
                                                <p className="text-xs text-slate-500">
                                                    Good descriptions help customers decide what to order.
                                                </p>
                                            </div>

                                            {includeArabic && (
                                                <div className="space-y-1.5">
                                                    <label className="block text-sm font-semibold text-slate-800">Arabic description</label>
                                                    <textarea
                                                        value={itemDescriptionAr}
                                                        onChange={(e) => setItemDescriptionAr(e.target.value)}
                                                        placeholder="أضف وصفًا موجزًا للعنصر باللغة العربية"
                                                        rows={3}
                                                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white resize-none"
                                                    />
                                                    <p className="text-xs text-slate-500">
                                                        Optional notes that help Arabic-speaking guests understand the item.
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Category */}
                                            <div className="space-y-1.5">
                                                <label className="block text-sm font-semibold text-slate-800">
                                                    Category<span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={itemCategory}
                                                    onChange={(e) => setItemCategory(e.target.value)}
                                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white"
                                                >
                                                    <option value="default" disabled>Select a category</option>
                                                    <option value="snacks">Snacks — 0 items</option>
                                                    <option value="mains">Mains — 0 items</option>
                                                </select>
                                                <p className="text-xs text-slate-500">
                                                    Items can be assigned to multiple categories later.
                                                </p>
                                            </div>

                                            {/* Pricing */}
                                            <div className="space-y-1.5">
                                                <label className="block text-sm font-semibold text-slate-800">Pricing</label>
                                                <div className="relative">
                                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-medium">QAR</span>
                                                    <input
                                                        type="number"
                                                        value={itemPrice}
                                                        onChange={(e) => setItemPrice(e.target.value)}
                                                        placeholder="0.00"
                                                        step="0.01"
                                                        min="0"
                                                        className="w-full rounded-lg border border-slate-200 pl-12 pr-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 bg-white"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        </div>
                                        </div>
                                    )}

                                    {createItemTab === 'options' && (
                                        /* Options / Customization Groups */
                                        <div className="space-y-4 pt-4">
                                            <div className="flex justify-between items-center">
                                                <label className="block text-sm font-semibold text-slate-800 flex items-center gap-2">
                                                    <Layers size={16} className="text-slate-500" />
                                                    Customization Options
                                                </label>
                                                <div className="flex gap-2">
                                                    {copiedGroup && (
                                                        <button 
                                                            type="button"
                                                            onClick={pasteChoiceGroup}
                                                            className="text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full font-medium hover:bg-blue-100 flex items-center gap-1 transition-colors"
                                                        >
                                                            <ClipboardPaste size={12} /> Paste Group
                                                        </button>
                                                    )}
                                                    <button 
                                                        type="button" 
                                                        onClick={addChoiceGroup} 
                                                        className="text-xs bg-slate-900 text-white px-3 py-1.5 rounded-full font-medium hover:bg-slate-800 flex items-center gap-1 transition-colors shadow-sm"
                                                    >
                                                        <Plus size={12} /> Add Option Group
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                {itemChoiceGroups.length === 0 && (
                                                    <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                                                        <p className="text-sm text-slate-500 font-medium">No customization options added yet.</p>
                                                        <p className="text-xs text-slate-400 mt-1">Add groups like "Choose Size" or "Extra Toppings"</p>
                                                    </div>
                                                )}
                                                {itemChoiceGroups.map((cg) => (
                                                    <div key={cg.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                                                        <div className="bg-slate-50/80 p-3 border-b border-slate-100 flex items-start gap-3">
                                                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                <input 
                                                                    value={cg.name.en} 
                                                                    onChange={e => updateChoiceGroup(cg.id, 'name.en', e.target.value)}
                                                                    className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                                                    placeholder="Group Name (e.g. Choose Sauce)"
                                                                />
                                                                {includeArabic && (
                                                                    <input 
                                                                        value={cg.name.ar || ''} 
                                                                        onChange={e => updateChoiceGroup(cg.id, 'name.ar', e.target.value)}
                                                                        className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-right font-arabic focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                                                        placeholder="اسم المجموعة"
                                                                        dir="rtl"
                                                                    />
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-1">
                                                                <button 
                                                                    type="button" 
                                                                    onClick={() => setCopiedGroup(cg)} 
                                                                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" 
                                                                    title="Copy Group"
                                                                >
                                                                    <Copy size={16} />
                                                                </button>
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => deleteChoiceGroup(cg.id)}
                                                                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                    title="Delete Group"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        
                                                        <div className="p-4 space-y-4">
                                                            <div className="flex flex-wrap gap-4 text-xs text-slate-600 items-center pb-3 border-b border-slate-50">
                                                                <label className="flex items-center gap-2 cursor-pointer select-none bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100 hover:bg-slate-100 transition-colors">
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={cg.required} 
                                                                        onChange={e => {
                                                                            const isReq = e.target.checked;
                                                                            updateChoiceGroup(cg.id, 'required', isReq);
                                                                            updateChoiceGroup(cg.id, 'optional', !isReq);
                                                                        }} 
                                                                        className="rounded text-slate-900 focus:ring-slate-900/20 border-slate-300" 
                                                                    />
                                                                    <span className="font-medium">Required</span>
                                                                </label>
                                                                <label className="flex items-center gap-2 cursor-pointer select-none bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100 hover:bg-slate-100 transition-colors">
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={cg.optional} 
                                                                        onChange={e => {
                                                                            const isOpt = e.target.checked;
                                                                            updateChoiceGroup(cg.id, 'optional', isOpt);
                                                                            updateChoiceGroup(cg.id, 'required', !isOpt);
                                                                        }} 
                                                                        className="rounded text-slate-900 focus:ring-slate-900/20 border-slate-300" 
                                                                    />
                                                                    <span className="font-medium">Optional</span>
                                                                </label>
                                                                <div className="h-6 w-px bg-slate-200 mx-2 hidden sm:block"></div>
                                                                <div className="flex items-center gap-3">
                                                                    <div className="flex items-center gap-2 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">
                                                                        <span className="font-medium text-slate-500">Min</span>
                                                                        <input 
                                                                            type="number" 
                                                                            min="0"
                                                                            value={cg.min} 
                                                                            onChange={e => updateChoiceGroup(cg.id, 'min', parseInt(e.target.value) || 0)} 
                                                                            className="w-12 bg-white border border-slate-200 rounded px-1 py-0.5 text-center focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                                                                        />
                                                                    </div>
                                                                    <div className="flex items-center gap-2 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100">
                                                                        <span className="font-medium text-slate-500">Max</span>
                                                                        <input 
                                                                            type="number" 
                                                                            min="1"
                                                                            value={cg.max} 
                                                                            onChange={e => updateChoiceGroup(cg.id, 'max', parseInt(e.target.value) || 0)} 
                                                                            className="w-12 bg-white border border-slate-200 rounded px-1 py-0.5 text-center focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="space-y-2">
                                                                {cg.options.map((opt) => (
                                                                    <div key={opt.id} className="flex gap-2 items-center group">
                                                                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                                            <input 
                                                                                value={opt.name.en}
                                                                                onChange={e => updateOption(cg.id, opt.id, 'name.en', e.target.value)}
                                                                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                                                                placeholder="Option Name"
                                                                            />
                                                                            {includeArabic && (
                                                                                <input 
                                                                                    value={opt.name.ar || ''}
                                                                                    onChange={e => updateOption(cg.id, opt.id, 'name.ar', e.target.value)}
                                                                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-right font-arabic focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                                                                    placeholder="اسم الخيار"
                                                                                    dir="rtl"
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        <div className="flex items-center border border-slate-200 rounded-lg w-24 overflow-hidden bg-white shrink-0">
                                                                            <span className="bg-slate-50 px-2 py-2 text-xs text-slate-500 border-r border-slate-100 shrink-0 select-none">
                                                                                +
                                                                            </span>
                                                                            <input 
                                                                                type="number"
                                                                                value={opt.priceModifier}
                                                                                onChange={e => updateOption(cg.id, opt.id, 'priceModifier', parseFloat(e.target.value))}
                                                                                className="w-full px-2 py-2 text-sm outline-none"
                                                                                placeholder="0"
                                                                            />
                                                                        </div>
                                                                        <button 
                                                                            type="button"
                                                                            onClick={() => deleteOption(cg.id, opt.id)}
                                                                            className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                                                            title="Delete Option"
                                                                        >
                                                                            <X size={16} />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                                <button 
                                                                    type="button" 
                                                                    onClick={() => addOption(cg.id)} 
                                                                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-3 py-2 rounded-lg flex items-center gap-2 transition-colors mt-2"
                                                                >
                                                                    <Plus size={14} /> Add Option
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    </div>

                                {/* Footer */}
                                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 mt-2">
                                    <button
                                        type="button"
                                        onClick={handleCancelCreateItem}
                                        className="px-4 py-2 rounded-full text-sm font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSaveItem}
                                        disabled={!itemName.trim() || itemCategory === 'default'}
                                        className={`px-5 py-2 rounded-full text-sm font-semibold shadow-sm transition-colors ${
                                            !itemName.trim() || itemCategory === 'default'
                                                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                                : 'bg-slate-900 text-white hover:bg-slate-800'
                                        }`}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        )}

                        {activeTab === 'options' && (
                            <div className="space-y-6">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h2 className="text-lg font-bold text-slate-900">Global Customization Options</h2>
                                        <p className="text-sm text-slate-500">Create option groups that can be reused across multiple items.</p>
                                    </div>
                                    <div className="flex gap-2">
                                        {copiedGroup && (
                                            <button 
                                                type="button"
                                                onClick={pasteMenuChoiceGroup}
                                                className="text-sm bg-blue-50 text-blue-700 px-4 py-2 rounded-full font-medium hover:bg-blue-100 flex items-center gap-2 transition-colors"
                                            >
                                                <ClipboardPaste size={16} /> Paste Group
                                            </button>
                                        )}
                                        <button 
                                            type="button" 
                                            onClick={addMenuChoiceGroup} 
                                            className="text-sm bg-slate-900 text-white px-4 py-2 rounded-full font-medium hover:bg-slate-800 flex items-center gap-2 transition-colors shadow-sm"
                                        >
                                            <Plus size={16} /> Add Option Group
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {menuChoiceGroups.length === 0 && (
                                        <div className="text-center py-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
                                                <Layers size={32} />
                                            </div>
                                            <h3 className="text-lg font-semibold text-slate-900 mb-1">No global options yet</h3>
                                            <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
                                                Create groups like "Pizza Sizes", "Salad Dressings", or "Cooking Preference" to use them on any item.
                                            </p>
                                            <button 
                                                onClick={addMenuChoiceGroup}
                                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold shadow-sm hover:bg-slate-800 transition-colors"
                                            >
                                                <Plus size={16} /> Create first group
                                            </button>
                                        </div>
                                    )}
                                    {menuChoiceGroups.map((cg) => (
                                        <div key={cg.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm">
                                            <div className="bg-slate-50/80 p-4 border-b border-slate-100 flex items-start gap-4">
                                                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <input 
                                                        value={cg.name.en} 
                                                        onChange={e => updateMenuChoiceGroup(cg.id, 'name.en', e.target.value)}
                                                        className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                                        placeholder="Group Name (e.g. Choose Sauce)"
                                                    />
                                                    {includeArabic && (
                                                        <input 
                                                            value={cg.name.ar || ''} 
                                                            onChange={e => updateMenuChoiceGroup(cg.id, 'name.ar', e.target.value)}
                                                            className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-medium text-right font-arabic focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                                            placeholder="اسم المجموعة"
                                                            dir="rtl"
                                                        />
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <button 
                                                        type="button" 
                                                        onClick={() => setCopiedGroup(cg)} 
                                                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" 
                                                        title="Copy Group"
                                                    >
                                                        <Copy size={18} />
                                                    </button>
                                                    <button 
                                                        type="button"
                                                        onClick={() => deleteMenuChoiceGroup(cg.id)}
                                                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Delete Group"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            <div className="p-6 space-y-6">
                                                <div className="flex flex-wrap gap-6 text-sm text-slate-600 items-center pb-4 border-b border-slate-50">
                                                    <label className="flex items-center gap-2 cursor-pointer select-none bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100 hover:bg-slate-100 transition-colors">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={cg.required} 
                                                            onChange={e => {
                                                                const isReq = e.target.checked;
                                                                updateMenuChoiceGroup(cg.id, 'required', isReq);
                                                                updateMenuChoiceGroup(cg.id, 'optional', !isReq);
                                                            }} 
                                                            className="rounded text-slate-900 focus:ring-slate-900/20 border-slate-300" 
                                                        />
                                                        <span className="font-medium">Required</span>
                                                    </label>
                                                    <label className="flex items-center gap-2 cursor-pointer select-none bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100 hover:bg-slate-100 transition-colors">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={cg.optional} 
                                                            onChange={e => {
                                                                const isOpt = e.target.checked;
                                                                updateMenuChoiceGroup(cg.id, 'optional', isOpt);
                                                                updateMenuChoiceGroup(cg.id, 'required', !isOpt);
                                                            }} 
                                                            className="rounded text-slate-900 focus:ring-slate-900/20 border-slate-300" 
                                                        />
                                                        <span className="font-medium">Optional</span>
                                                    </label>
                                                    <div className="h-6 w-px bg-slate-200 hidden sm:block"></div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                                                            <span className="font-medium text-slate-500">Min Selection</span>
                                                            <input 
                                                                type="number" 
                                                                min="0"
                                                                value={cg.min} 
                                                                onChange={e => updateMenuChoiceGroup(cg.id, 'min', parseInt(e.target.value) || 0)} 
                                                                className="w-16 bg-white border border-slate-200 rounded px-2 py-0.5 text-center focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                                                            />
                                                        </div>
                                                        <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                                                            <span className="font-medium text-slate-500">Max Selection</span>
                                                            <input 
                                                                type="number" 
                                                                min="1"
                                                                value={cg.max} 
                                                                onChange={e => updateMenuChoiceGroup(cg.id, 'max', parseInt(e.target.value) || 0)} 
                                                                className="w-16 bg-white border border-slate-200 rounded px-2 py-0.5 text-center focus:outline-none focus:ring-1 focus:ring-slate-900/10"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="space-y-3">
                                                    {cg.options.map((opt) => (
                                                        <div key={opt.id} className="flex gap-3 items-center group">
                                                            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                                <input 
                                                                    value={opt.name.en}
                                                                    onChange={e => updateMenuOption(cg.id, opt.id, 'name.en', e.target.value)}
                                                                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                                                    placeholder="Option Name"
                                                                />
                                                                {includeArabic && (
                                                                    <input 
                                                                        value={opt.name.ar || ''}
                                                                        onChange={e => updateMenuOption(cg.id, opt.id, 'name.ar', e.target.value)}
                                                                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-right font-arabic focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                                                        placeholder="اسم الخيار"
                                                                        dir="rtl"
                                                                    />
                                                                )}
                                                            </div>
                                                            <div className="flex items-center border border-slate-200 rounded-lg w-32 overflow-hidden bg-white shrink-0">
                                                                <span className="bg-slate-50 px-3 py-2 text-sm text-slate-500 border-r border-slate-100 shrink-0 select-none">
                                                                    + QAR
                                                                </span>
                                                                <input 
                                                                    type="number"
                                                                    value={opt.priceModifier}
                                                                    onChange={e => updateMenuOption(cg.id, opt.id, 'priceModifier', parseFloat(e.target.value))}
                                                                    className="w-full px-2 py-2 text-sm outline-none"
                                                                    placeholder="0"
                                                                />
                                                            </div>
                                                            <button 
                                                                type="button"
                                                                onClick={() => deleteMenuOption(cg.id, opt.id)}
                                                                className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                                                title="Delete Option"
                                                            >
                                                                <X size={18} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button 
                                                        type="button" 
                                                        onClick={() => addMenuOption(cg.id)} 
                                                        className="text-sm font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors mt-2"
                                                    >
                                                        <Plus size={16} /> Add Option
                                                    </button>
                                                </div>

                                                <div className="pt-4 border-t border-slate-100">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <label className="text-sm font-semibold text-slate-800">Select items to apply to</label>
                                                        <button 
                                                            onClick={() => openItemSelector(cg.id, cg.targetItemIds)}
                                                            className="text-xs font-semibold text-slate-900 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-full transition-colors"
                                                        >
                                                            + Add items
                                                        </button>
                                                    </div>
                                                    {(!cg.targetItemIds || cg.targetItemIds.length === 0) ? (
                                                        <p className="text-xs text-slate-400 italic">No items selected. This group will not appear on any item.</p>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-2">
                                                            {cg.targetItemIds.map(itemId => {
                                                                // Find item name for display
                                                                let itemName = 'Unknown Item';
                                                                categories.forEach(c => {
                                                                    const found = c.items.find(i => i.id === itemId);
                                                                    if (found) itemName = found.name.en;
                                                                });
                                                                return (
                                                                    <span key={itemId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200">
                                                                        {itemName}
                                                                        <button 
                                                                            onClick={() => {
                                                                                const newIds = cg.targetItemIds?.filter(id => id !== itemId) || [];
                                                                                updateMenuChoiceGroup(cg.id, 'targetItemIds', newIds);
                                                                            }}
                                                                            className="hover:text-red-500"
                                                                        >
                                                                            <X size={12} />
                                                                        </button>
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab === 'canvas' && (
                            <>
                                {canvasView === 'editor' ? (
                                  <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCorners}
                                    measuring={{ droppable: { strategy: MeasuringStrategy.Always } }} 
                                    onDragStart={handleDragStart}
                                    onDragOver={handleDragOver}
                                    onDragEnd={handleDragEnd}
                                  >
                                    <MenuCanvas 
                                        data={{...data, categories: data.categories || []}} 
                                        language={language} 
                                        printRef={printRef} 
                                        currency="QAR" 
                                        viewFilters={viewFilters}
                                    />
                                    <DragOverlay dropAnimation={dropAnimation}>
                                       {activeId ? (
                                           <div className="bg-slate-900 text-white px-4 py-2 rounded-lg shadow-xl font-bold text-sm">
                                               Moving Item...
                                           </div>
                                       ) : null}
                                    </DragOverlay>
                                  </DndContext>
                                ) : (
                                    <OrderingPage 
                                        ref={orderingRef}
                                        data={{...data, categories: data.categories || []}} 
                                        onBack={() => setActiveTab('categories')} 
                                        currency="QAR" 
                                        language={language} 
                                        viewFilters={viewFilters}
                                        onOpenImageEditor={onOpenImageEditor}
                                    />
                                )}
                            </>
                        )}

                        {activeTab === 'settings' && (
                            <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-5">
                                    <h3 className="text-lg font-bold text-slate-900">Settings</h3>
                                    <p className="text-sm text-slate-500 mt-1">Manage your menu configuration, preferences, and data.</p>
                                </div>

                                <div className="divide-y divide-slate-100">
                                    {/* General Configuration Section */}
                                    <div className="p-6">
                                        <div className="flex flex-col md:flex-row md:items-start gap-6">
                                            <div className="md:w-1/3">
                                                <h4 className="text-sm font-semibold text-slate-900 mb-1">General Configuration</h4>
                                                <p className="text-xs text-slate-500">Set the base currency and available languages for your menu.</p>
                                            </div>
                                            
                                            <div className="md:w-2/3 space-y-5">
                                                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                                                    <label className="block text-xs font-medium text-slate-700 mb-2">Currency Symbol</label>
                                                    <div className="relative max-w-xs">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <Coins size={14} className="text-slate-400" />
                                                        </div>
                                                        <input 
                                                            type="text" 
                                                            value={data.currency || 'QAR'} 
                                                            onChange={(e) => {
                                                                if (onUpdateData) {
                                                                    onUpdateData({ ...data, currency: e.target.value });
                                                                }
                                                            }}
                                                            className="block w-full pl-9 pr-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-900 focus:border-slate-900 text-sm transition-shadow"
                                                            placeholder="e.g. USD, EUR, QAR"
                                                        />
                                                    </div>
                                                    <p className="mt-1.5 text-[10px] text-slate-500">This symbol will be displayed next to all prices.</p>
                                                </div>

                                                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                                                    <label className="block text-xs font-medium text-slate-700 mb-3">Supported Languages</label>
                                                    <div className="space-y-2">
                                                        <label className="flex items-center justify-between p-2.5 rounded-lg border border-slate-200 bg-slate-50 cursor-not-allowed">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-700">EN</div>
                                                                <div>
                                                                    <span className="block text-sm font-medium text-slate-900">English</span>
                                                                    <span className="block text-[10px] text-slate-500">Default language</span>
                                                                </div>
                                                            </div>
                                                            <input type="checkbox" checked disabled className="w-3.5 h-3.5 text-slate-900 rounded border-slate-300 focus:ring-slate-900" />
                                                        </label>

                                                        <label className={`flex items-center justify-between p-2.5 rounded-lg border cursor-pointer transition-all ${data.languages?.includes('ar') ? 'border-slate-900 bg-slate-50 ring-1 ring-slate-900' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`}>
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-700">AR</div>
                                                                <div>
                                                                    <span className="block text-sm font-medium text-slate-900">Arabic</span>
                                                                    <span className="block text-[10px] text-slate-500">Enable right-to-left support</span>
                                                                </div>
                                                            </div>
                                                            <input 
                                                                type="checkbox" 
                                                                checked={data.languages?.includes('ar') || false} 
                                                                onChange={(e) => {
                                                                    const currentLangs = data.languages || ['en'];
                                                                    let newLangs;
                                                                    if (e.target.checked) {
                                                                        newLangs = [...currentLangs, 'ar'];
                                                                    } else {
                                                                        newLangs = currentLangs.filter(l => l !== 'ar');
                                                                    }
                                                                    if (onUpdateData) {
                                                                        onUpdateData({ ...data, languages: newLangs });
                                                                    }
                                                                }}
                                                                className="w-3.5 h-3.5 text-slate-900 rounded border-slate-300 focus:ring-slate-900" 
                                                            />
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Data Management Section */}
                                    <div className="p-6">
                                        <div className="flex flex-col md:flex-row md:items-start gap-6">
                                            <div className="md:w-1/3">
                                                <h4 className="text-sm font-semibold text-slate-900 mb-1">Data Management</h4>
                                                <p className="text-xs text-slate-500">Import data from external sources or backup your current menu.</p>
                                            </div>
                                            
                                            <div className="md:w-2/3 space-y-3">
                                                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                                                            <FileSpreadsheet size={16} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h5 className="text-sm font-medium text-slate-900 mb-0.5">Import from Excel</h5>
                                                            <p className="text-[10px] text-slate-500 mb-3">Upload an Excel file to replace the current menu data. This will overwrite all existing categories, items, and options.</p>
                                                            
                                                            <label className="inline-flex">
                                                                <input
                                                                    type="file"
                                                                    accept=".xlsx, .xls"
                                                                    onChange={handleImport}
                                                                    className="hidden"
                                                                />
                                                                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-700 text-xs font-medium hover:bg-slate-50 hover:text-slate-900 transition-colors cursor-pointer">
                                                                    <Upload size={14} />
                                                                    Select Excel File
                                                                </span>
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                                                    <div className="flex items-start gap-3">
                                                        <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                                            <FileJson size={16} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <h5 className="text-sm font-medium text-slate-900 mb-0.5">Export Backup</h5>
                                                            <p className="text-[10px] text-slate-500 mb-3">Download a raw JSON file of your menu data for backup and restoration purposes.</p>
                                                            
                                                            <button
                                                                onClick={handleDownloadJson}
                                                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-700 text-xs font-medium hover:bg-slate-50 hover:text-slate-900 transition-colors"
                                                            >
                                                                <FileJson size={14} />
                                                                Download JSON
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

            {/* Item Selector Modal */}
            {itemSelectorModal.isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4 py-6 animate-in fade-in duration-200">
                    <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[80vh]">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                            <h3 className="text-lg font-bold text-slate-900">Add items</h3>
                            <button onClick={() => setItemSelectorModal({ isOpen: false, groupId: null })} className="p-2 text-slate-400 hover:text-slate-600 rounded-full">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-4 border-b border-slate-100 bg-slate-50/50 shrink-0">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                <input 
                                    type="text"
                                    value={itemSearchQuery}
                                    onChange={(e) => setItemSearchQuery(e.target.value)}
                                    placeholder="Search items..."
                                    className="w-full bg-white border border-slate-200 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                />
                            </div>
                        </div>

                        <div className="overflow-y-auto p-2 flex-1">
                            {categories.map(category => {
                                const filteredItems = category.items.filter(item => 
                                    item.name.en.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
                                    (item.name.ar && item.name.ar.includes(itemSearchQuery))
                                );

                                if (filteredItems.length === 0) return null;

                                return (
                                    <div key={category.id} className="mb-2">
                                        <div className="px-4 py-2 bg-slate-50/80 rounded-lg mb-1">
                                            <h4 className="text-sm font-semibold text-slate-700">{category.name.en}</h4>
                                        </div>
                                        <div className="space-y-0.5">
                                            {filteredItems.map(item => {
                                                const isSelected = selectedItemIds.has(item.id);
                                                return (
                                                    <label 
                                                        key={item.id} 
                                                        className={`flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                                                            isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-slate-50'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                                                isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'
                                                            }`}>
                                                                {isSelected && <Check size={12} strokeWidth={3} />}
                                                            </div>
                                                            <span className={`text-sm ${isSelected ? 'font-medium text-slate-900' : 'text-slate-600'}`}>
                                                                {item.name.en}
                                                            </span>
                                                        </div>
                                                        <span className="text-sm font-medium text-slate-500">
                                                            QAR {item.price.toFixed(2)}
                                                        </span>
                                                        <input 
                                                            type="checkbox" 
                                                            className="hidden" 
                                                            checked={isSelected}
                                                            onChange={() => toggleItemSelection(item.id)}
                                                        />
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                            {categories.every(c => c.items.filter(i => i.name.en.toLowerCase().includes(itemSearchQuery.toLowerCase())).length === 0) && (
                                <div className="text-center py-8 text-slate-500 text-sm">
                                    No items found matching "{itemSearchQuery}"
                                </div>
                            )}
                        </div>

                        <div className="p-4 border-t border-slate-100 bg-white shrink-0 flex justify-between items-center">
                            <button 
                                onClick={() => setItemSelectorModal({ isOpen: false, groupId: null })}
                                className="text-sm font-semibold text-slate-500 hover:text-slate-800 px-4 py-2"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={saveItemSelection}
                                className="bg-slate-900 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-slate-800 shadow-sm"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Category Item Selector Modal */}
            {categoryItemSelectorOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4 py-6 animate-in fade-in duration-200">
                    <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[80vh]">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
                            <h3 className="text-lg font-bold text-slate-900">Add items to category</h3>
                            <button onClick={() => setCategoryItemSelectorOpen(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-full">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-4 border-b border-slate-100 bg-slate-50/50 shrink-0">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                <input 
                                    type="text"
                                    value={itemSearchQuery}
                                    onChange={(e) => setItemSearchQuery(e.target.value)}
                                    placeholder="Search items..."
                                    className="w-full bg-white border border-slate-200 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                                />
                            </div>
                        </div>

                        <div className="overflow-y-auto p-2 flex-1">
                            {categories.map(category => {
                                const filteredItems = category.items.filter(item => 
                                    item.name.en.toLowerCase().includes(itemSearchQuery.toLowerCase()) ||
                                    (item.name.ar && item.name.ar.includes(itemSearchQuery))
                                );

                                if (filteredItems.length === 0) return null;

                                return (
                                    <div key={category.id} className="mb-2">
                                        <div className="px-4 py-2 bg-slate-50/80 rounded-lg mb-1">
                                            <h4 className="text-sm font-semibold text-slate-700">{category.name.en}</h4>
                                        </div>
                                        <div className="space-y-0.5">
                                            {filteredItems.map(item => {
                                                const isSelected = categorySelectedItems.has(item.id);
                                                return (
                                                    <label 
                                                        key={item.id} 
                                                        className={`flex items-center justify-between px-4 py-3 rounded-lg cursor-pointer transition-colors ${
                                                            isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-slate-50'
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                                                isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'
                                                            }`}>
                                                                {isSelected && <Check size={12} strokeWidth={3} />}
                                                            </div>
                                                            <span className={`text-sm ${isSelected ? 'font-medium text-slate-900' : 'text-slate-600'}`}>
                                                                {item.name.en}
                                                            </span>
                                                        </div>
                                                        <span className="text-sm font-medium text-slate-500">
                                                            QAR {item.price.toFixed(2)}
                                                        </span>
                                                        <input 
                                                            type="checkbox" 
                                                            className="hidden" 
                                                            checked={isSelected}
                                                            onChange={() => toggleCategoryItemSelection(item.id)}
                                                        />
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                            {categories.every(c => c.items.filter(i => i.name.en.toLowerCase().includes(itemSearchQuery.toLowerCase())).length === 0) && (
                                <div className="text-center py-8 text-slate-500 text-sm">
                                    No items found matching "{itemSearchQuery}"
                                </div>
                            )}
                        </div>

                        <div className="p-4 border-t border-slate-100 bg-white shrink-0 flex justify-between items-center">
                            <button 
                                onClick={() => setCategoryItemSelectorOpen(false)}
                                className="text-sm font-semibold text-slate-500 hover:text-slate-800 px-4 py-2"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={saveCategoryItemSelection}
                                className="bg-slate-900 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-slate-800 shadow-sm"
                            >
                                Add Selected ({categorySelectedItems.size})
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {modalType && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 animate-in fade-in duration-200">
                    <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                            <div>
                                <p className="text-sm text-slate-500">
                                    {modalType === 'image'
                                        ? 'Missing images'
                                        : modalType === 'name'
                                        ? 'Missing names'
                                        : 'Missing descriptions'}
                                </p>
                                <h3 className="text-xl font-bold text-slate-900">Review items</h3>
                            </div>
                            <button onClick={() => setModalType(null)} className="p-2 text-slate-400 hover:text-slate-600 rounded-full">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto p-6 space-y-4">
                            {modalIssues.length === 0 ? (
                                <p className="text-sm text-slate-500">Nothing to fix here.</p>
                            ) : (
                                modalIssues.map((issue) => (
                                    <div key={issue.itemId} className="rounded-2xl border border-slate-100 p-4 bg-slate-50 space-y-1">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-semibold text-slate-900">{issue.itemName}</p>
                                                <p className="text-xs text-slate-500">{issue.categoryName}</p>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    onOpenEditor(issue.itemId);
                                                    setModalType(null);
                                                }}
                                                className="text-xs font-semibold text-purple-600 hover:text-purple-800"
                                            >
                                                Fix it
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-2 text-xs">
                                            {issue.reasons.map((reason) => (
                                                <span
                                                    key={reason}
                                                    className="px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-600"
                                                >
                                                    {reasonLabels[reason]}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Export Modal */}
            {showExportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-emerald-600">
                                <FileSpreadsheet size={20} />
                                <h3 className="font-bold text-lg text-slate-900">Export Menu</h3>
                            </div>
                            <button 
                                onClick={() => setShowExportModal(false)}
                                className="text-slate-400 hover:text-slate-600 transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-500 mb-2">Choose an export format for your menu data.</p>
                            
                            <button
                                onClick={() => {
                                    exportToExcel(data as any, menuName);
                                    setShowExportModal(false);
                                }}
                                className="w-full flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all group text-left"
                            >
                                <div>
                                    <p className="font-bold text-slate-900 group-hover:text-emerald-700">Template 1 (Standard)</p>
                                    <p className="text-xs text-slate-500 mt-1">Current export format with yellow styling</p>
                                </div>
                                <FileSpreadsheet className="text-slate-300 group-hover:text-emerald-500" size={24} />
                            </button>

                            <button
                                onClick={() => {
                                    exportToExcelTemplate2(data as any, menuName);
                                    setShowExportModal(false);
                                }}
                                className="w-full flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-purple-500 hover:bg-purple-50 transition-all group text-left"
                            >
                                <div>
                                    <p className="font-bold text-slate-900 group-hover:text-purple-700">Template 2 (New Layout)</p>
                                    <p className="text-xs text-slate-500 mt-1">Purple headers, specific columns, no legend</p>
                                </div>
                                <FileSpreadsheet className="text-slate-300 group-hover:text-purple-500" size={24} />
                            </button>

                            <button
                                onClick={() => {
                                    exportBothTemplates(data as any, menuName);
                                    setShowExportModal(false);
                                }}
                                className="w-full flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition-all group text-left"
                            >
                                <div>
                                    <p className="font-bold text-slate-900 group-hover:text-blue-700">Both Templates</p>
                                    <p className="text-xs text-slate-500 mt-1">Single file with two sheets (Template 1 & 2)</p>
                                </div>
                                <Layers className="text-slate-300 group-hover:text-blue-500" size={24} />
                            </button>

                            <button
                                onClick={() => {
                                    exportMenuBundle(data as any, {
                                        includeExcel: false,
                                        includeImageColumn: false,
                                        includeOriginalImages: true,
                                        folderByCategory: true,
                                        includeFlatFolder: false,
                                        aggregators: [],
                                        currency: 'QAR'
                                    });
                                    setShowExportModal(false);
                                }}
                                className="w-full flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-orange-500 hover:bg-orange-50 transition-all group text-left"
                            >
                                <div>
                                    <p className="font-bold text-slate-900 group-hover:text-orange-700">Download Images</p>
                                    <p className="text-xs text-slate-500 mt-1">ZIP file with images organized by category</p>
                                </div>
                                <ImageIcon className="text-slate-300 group-hover:text-orange-500" size={24} />
                            </button>
                        </div>

                        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
                            <button
                                onClick={() => setShowExportModal(false)}
                                className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-200 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {deleteConfirmation && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6 animate-in fade-in duration-200">
                    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-6 text-center">
                            <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-4">
                                <Trash2 size={24} />
                            </div>
                            <h3 className="text-lg font-bold text-slate-900 mb-2">
                                Delete {deleteConfirmation.type === 'category' ? 'Category' : 'Item'}?
                            </h3>
                            <p className="text-sm text-slate-500 mb-6">
                                Are you sure you want to delete this {deleteConfirmation.type}? This action cannot be undone.
                            </p>
                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={cancelDelete}
                                    className="px-4 py-2 rounded-full text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 rounded-full text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
